import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ThumbDownAlt from '@mui/icons-material/ThumbDownAlt';
import ThumbDownIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpAlt from '@mui/icons-material/ThumbUpAlt';
import ThumbUpIcon from '@mui/icons-material/ThumbUpOutlined';
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/system/Stack';
import { useTheme } from '@mui/material/styles';

import { THUMBS_DOWN_REASONS, THUMBS_UP_REASONS } from 'app-components/utils/aiFeedback';
import { tracker } from 'app-components/utils/core';
import type { ChartRequest } from 'app-components/flux/ai-chat/aiChatTypes';
import { trackingEvents } from 'containers/SimplifiAiAssistant/AiAssistant/aiEvents';
import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';
import React, { useEffect, useMemo, useState } from 'react';
import useSpeechSynthesis from 'utils/speech/useSpeechSynthesis';
import useVoiceState from 'utils/speech/useVoiceState';
import { useChatSession } from 'components/ChatLauncher/chatSession';

type FeedbackThumb = 'up' | 'down';

type ChatFeedbackProps = {
  messageId: string | number;
  aiResponse: string;
  charts?: ChartRequest[];
  onSubmit?: (payload: {
    messageId: string | number;
    thumb: FeedbackThumb;
    reason?: string | null;
    additionalFeedback?: string | null;
  }) => void;
  onManageMemories?: () => void;
};

const feedbackConfig = {
  positive: THUMBS_UP_REASONS,
  negative: THUMBS_DOWN_REASONS,
};

const ChatFeedback: React.FC<ChatFeedbackProps> = ({ messageId, aiResponse, charts, onSubmit, onManageMemories }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedThumb, setSelectedThumb] = useState<FeedbackThumb | null>(null);
  const [additionalFeedback, setAdditionalFeedback] = useState('');
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [voiceState] = useVoiceState();
  const { conversationId } = useChatSession();
  const speak = useSpeechSynthesis();

  const feedbackReasons = useMemo(
    () => (selectedThumb === 'up' ? feedbackConfig.positive : feedbackConfig.negative),
    [selectedThumb],
  );
  const defaultProps = {
    conversationId,
    messageId,
  };

  const isSubmitDisabled = !selectedReason && additionalFeedback.trim().length < 3;

  useEffect(() => {
    if (charts && charts.length > 0) {
      tracker.track(trackingEvents.AIChatChartRendered, {
        conversationId,
        messageId,
        hasChart: true,
        chartType: charts[0].chartType,
      });
    }
  }, [charts, conversationId, messageId]);

  const handleThumbClick = (thumb: FeedbackThumb) => {
    if (selectedThumb !== thumb) {
      setSelectedReason(null);
      setAdditionalFeedback('');
      setSubmitted(false);
      setExpanded(true);
    }
    if (thumb === 'up') {
      tracker.track(trackingEvents.AIChatThumbsUp, { ...defaultProps });
    }

    if (thumb === 'down') {
      tracker.track(trackingEvents.AIChatThumbsDown, { ...defaultProps });
    }
    setSelectedThumb(thumb);
  };

  const handleSubmit = () => {
    if (!selectedThumb) return;
    setSubmitted(true);
    onSubmit?.({
      ...defaultProps,
      thumb: selectedThumb,
      reason: selectedReason,
      additionalFeedback: additionalFeedback.trim() || null,
    });

    if (selectedThumb === 'up') {
      tracker.track(trackingEvents.AIChatThumbsUpExplanation, {
        ...defaultProps,
        reason: selectedReason,
        feedback: additionalFeedback.trim() || null,
      });
    }
    if (selectedThumb === 'down') {
      tracker.track(trackingEvents.AIChatThumbsDownExplanation, {
        ...defaultProps,
        reason: selectedReason,
        feedback: additionalFeedback.trim() || null,
      });
    }
  };

  const handleClose = () => {
    setExpanded(false);
    setSelectedReason(null);
    setAdditionalFeedback('');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(aiResponse);
    dispatchSimpleNotification('Message copied to clipboard');
  };

  const handleSpeak = () => {
    if (voiceState === 'SPEAKING') {
      speak(null);
    } else {
      speak(aiResponse);
    }
  };

  const ThumbUp = selectedThumb === 'up' ? ThumbUpAlt : ThumbUpIcon;
  const ThumbDown = selectedThumb === 'down' ? ThumbDownAlt : ThumbDownIcon;

  return (
    <Stack id='messageActions' sx={{ position: 'relative', alignItems: 'flex-start' }}>
      <Box
        sx={{
          display: 'flex',
          border: `1px solid ${theme.palette.border.light}`,
          borderRadius: '8px',
          backgroundColor: theme.palette.background.paper,
          width: 'fit-content',
        }}
      >
        <Tooltip title={'Good Response'} arrow>
          <IconButton size='small' onClick={() => handleThumbClick('up')}>
            <ThumbUp fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title={'Bad Response'} arrow>
          <IconButton size='small' onClick={() => handleThumbClick('down')}>
            <ThumbDown fontSize='small' />
          </IconButton>
        </Tooltip>
        <Divider orientation='vertical' flexItem variant='middle' />
        <Tooltip title={'Copy'} arrow>
          <IconButton size='small' onClick={handleCopy}>
            <ContentCopyOutlinedIcon fontSize='small' color='inherit' />
          </IconButton>
        </Tooltip>
        <Divider orientation='vertical' flexItem variant='middle' />
        <Tooltip title={voiceState === 'SPEAKING' ? 'Stop Reading' : 'Read Aloud'} arrow>
          <IconButton size='small' onClick={handleSpeak}>
            {voiceState === 'SPEAKING' ? (
              <VolumeOffOutlinedIcon fontSize='small' color='inherit' />
            ) : (
              <VolumeUpOutlinedIcon fontSize='small' color='inherit' />
            )}
          </IconButton>
        </Tooltip>
        {onManageMemories && (
          <>
            <Divider orientation='vertical' flexItem variant='middle' />
            <Tooltip title={'Manage Memories'} arrow>
              <IconButton size='small' onClick={onManageMemories}>
                <PsychologyIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {expanded && (
        <Box>
          <Paper sx={{ padding: 1, marginTop: 1 }} elevation={24}>
            {submitted && (
              <Typography variant='bodyRegular' sx={{ textAlign: 'center' }}>
                Thank you for your feedback
              </Typography>
            )}
            {!submitted && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                  <Typography variant='paragraphMedium'>Why did you choose this rating?</Typography>
                  <IconButton variant='tonal' onClick={handleClose} size='small'>
                    <CloseIcon fontSize='small' />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, marginBottom: 1, flexWrap: 'wrap' }}>
                  {feedbackReasons.map((reason) => (
                    <Chip
                      variant={selectedReason === reason ? 'outlined' : 'tonal'}
                      key={reason}
                      label={reason}
                      clickable
                      color={selectedReason === reason ? 'primary' : 'default'}
                      onClick={() => setSelectedReason(reason)}
                      size='small'
                    />
                  ))}
                </Box>
                <TextField
                  hiddenLabel
                  placeholder='Provide additional feedback'
                  multiline
                  maxRows={3}
                  rows={2}
                  fullWidth
                  value={additionalFeedback}
                  onChange={(e) => setAdditionalFeedback(e.target.value)}
                  sx={{
                    marginBottom: 2,
                    '& .MuiInputBase-root': {
                      padding: 1,
                    },
                  }}
                  size='small'
                />
                <Button
                  variant='contained'
                  color='primary'
                  fullWidth
                  onClick={handleSubmit}
                  size='medium'
                  disabled={!selectedThumb || isSubmitDisabled}
                >
                  Submit
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </Stack>
  );
};

export default ChatFeedback;
